import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useTQModelContext } from 'components/TQModels/context/TQModelContext';
import { PlanScenarioParams } from 'constants/routes';
import { useSPMCollaborationContext } from 'contexts/SPMCollaborationContext';
import { useSPMPlanContext } from 'contexts/SPMPlanContext';
import { ResourceTypes } from 'types/permissions';
import { SPMScenario } from 'types/SPM';
import { TQModel } from 'types/tqModels';

export type NavigationData = {
  activeScenario: SPMScenario;
  tqModel: TQModel;
  canViewQuotaModel: boolean;
  canViewCapacityModel: boolean;
  canViewTerritoriesModel: boolean;
};

export function useNavigationData(): NavigationData {
  const { scenarioId } = useParams<PlanScenarioParams>();
  const { userPermissions } = useTQModelContext();
  const { isCollaborationUser } = useSPMCollaborationContext();
  const { scenarios, baseScenario, quotaModel, capacityModel } = useSPMPlanContext();

  const activeScenario = useMemo(() => {
    if (!scenarios) {
      return undefined;
    }
    return scenarios.find((sc) => sc.id === scenarioId) || scenarios[0] || baseScenario;
  }, [scenarios, scenarioId, baseScenario]);

  const canViewModel = useMemo(() => {
    return (modelId: string, modelType: ResourceTypes) => {
      if (!isCollaborationUser) {
        return true;
      }

      if (!userPermissions?.shared_resources) {
        return false;
      }

      return userPermissions.shared_resources.some(
        (sharedResource) =>
          sharedResource.resource_path.includes(modelId) &&
          (sharedResource.resource_type === modelType ||
            (modelType === 'spm_model' && sharedResource.resource_type === 'spm_shared_view')),
      );
    };
  }, [userPermissions, isCollaborationUser]);

  return useMemo(() => {
    if (!activeScenario) {
      // This is just a fallback for tests. In production, this shouldn't happen since
      // the SPMPlanContext will provide the data.
      return {} as NavigationData;
    }
    return {
      activeScenario,
      tqModel: activeScenario.hierarchy_model,
      canViewQuotaModel: canViewModel(quotaModel.id, 'spm_model'),
      canViewCapacityModel: canViewModel(capacityModel.id, 'spm_model'),
      canViewTerritoriesModel: canViewModel(activeScenario.hierarchy_model.id, 'territory'),
    };
  }, [activeScenario, canViewModel, quotaModel, capacityModel]);
}
